/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useRef, useEffect, useState, useCallback } from 'react'

interface KnobStyles {
  min?: number
  max?: number
  width?: number
  height?: number
  fgColor?: string
  bgColor?: string
  skin?: 'tron' | 'default'
  thickness?: number
  lineCap?: 'butt' | 'round'
  angleOffset?: number
  angleArc?: number
  step?: number
  displayInput?: boolean
  displayPrevious?: boolean
  readOnly?: boolean
  cursor?: boolean | number
  font?: string
  fontWeight?: string
}

interface KnobProps {
  id?: string
  name: string
  value: number
  onChange: (value: number) => void
  styles?: KnobStyles
  disabled?: boolean
}

const defaultStyles: Required<KnobStyles> = {
  min: 0,
  max: 360,
  width: 75,
  height: 75,
  fgColor: '#106cc8',
  bgColor: '#EEEEEE',
  skin: 'tron',
  thickness: 0.2,
  lineCap: 'butt',
  angleOffset: 0,
  angleArc: 360,
  step: 1,
  displayInput: true,
  displayPrevious: false,
  readOnly: false,
  cursor: false,
  font: 'Arial',
  fontWeight: 'bold'
}

/**
 * Knob Component - A circular dial/knob control for value input
 *
 * This is a modern React implementation replacing the jQuery Knob plugin.
 * Supports rotation angle input with visual feedback.
 *
 * @example
 * ```tsx
 * <Knob
 *   name="rotateAngle"
 *   value={angle}
 *   onChange={(newAngle) => setAngle(newAngle)}
 *   styles={{ min: 0, max: 360, fgColor: '#106cc8' }}
 * />
 * ```
 */
export const Knob: React.FC<KnobProps> = ({ id, name, value, onChange, styles = {}, disabled = false }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [angle, setAngle] = useState(value)
  const [isDragging, setIsDragging] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  // Merge default styles with provided styles
  const config = { ...defaultStyles, ...styles }

  // Normalize angle to be within min-max range
  const normalizeValue = useCallback(
    (val: number): number => {
      let normalized = Math.round(val / config.step) * config.step
      normalized = Math.max(config.min, Math.min(config.max, normalized))
      return normalized
    },
    [config.min, config.max, config.step]
  )

  // Update internal state when value prop changes
  useEffect(() => {
    setAngle(normalizeValue(value))
  }, [value, normalizeValue])

  // Calculate angle from mouse/touch position
  const calculateAngleFromEvent = useCallback(
    (clientX: number, clientY: number): number => {
      if (!canvasRef.current) return angle

      const rect = canvasRef.current.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2

      const deltaX = clientX - centerX
      const deltaY = clientY - centerY

      // Calculate angle in degrees (0-360)
      let deg = Math.atan2(deltaY, deltaX) * (180 / Math.PI)
      deg = deg + 90 // Adjust so 0 degrees is at top
      if (deg < 0) deg += 360

      // Apply angle offset and arc
      deg = (deg + config.angleOffset) % 360

      // Map the angle to the value range
      const range = config.max - config.min
      const arcRatio = deg / config.angleArc
      const newValue = config.min + arcRatio * range

      return normalizeValue(newValue)
    },
    [angle, config, normalizeValue]
  )

  // Draw the knob on canvas
  const drawKnob = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const { width, height, fgColor, bgColor, thickness, skin, lineCap } = config
    const centerX = width / 2
    const centerY = height / 2
    const radius = Math.min(width, height) / 2
    const lineWidth = radius * thickness

    // Clear canvas
    ctx.clearRect(0, 0, width, height)

    // Calculate angle for current value
    const range = config.max - config.min
    const valueRatio = (angle - config.min) / range
    const angleRad = (valueRatio * config.angleArc - 90 + config.angleOffset) * (Math.PI / 180)

    if (skin === 'tron') {
      // Draw background arc (full circle or arc)
      ctx.beginPath()
      ctx.strokeStyle = bgColor
      ctx.lineWidth = lineWidth
      ctx.lineCap = lineCap
      const startAngle = (config.angleOffset - 90) * (Math.PI / 180)
      const endAngle = (config.angleOffset - 90 + config.angleArc) * (Math.PI / 180)
      ctx.arc(centerX, centerY, radius - lineWidth / 2, startAngle, endAngle)
      ctx.stroke()

      // Draw foreground arc (value indicator)
      ctx.beginPath()
      ctx.strokeStyle = fgColor
      ctx.lineWidth = lineWidth
      ctx.lineCap = lineCap
      ctx.arc(centerX, centerY, radius - lineWidth / 2, startAngle, angleRad)
      ctx.stroke()

      // Draw cursor/handle
      if (config.cursor) {
        const cursorSize = typeof config.cursor === 'number' ? config.cursor : lineWidth * 0.3
        const cursorX = centerX + (radius - lineWidth / 2) * Math.cos(angleRad)
        const cursorY = centerY + (radius - lineWidth / 2) * Math.sin(angleRad)

        ctx.beginPath()
        ctx.fillStyle = fgColor
        ctx.arc(cursorX, cursorY, cursorSize, 0, 2 * Math.PI)
        ctx.fill()
      }
    } else {
      // Default skin - simple circle with pointer
      // Draw background circle
      ctx.beginPath()
      ctx.strokeStyle = bgColor
      ctx.lineWidth = 2
      ctx.arc(centerX, centerY, radius - 5, 0, 2 * Math.PI)
      ctx.stroke()

      // Draw fill
      ctx.fillStyle = '#FFFFFF'
      ctx.fill()

      // Draw value indicator line
      ctx.beginPath()
      ctx.strokeStyle = fgColor
      ctx.lineWidth = 3
      ctx.lineCap = 'round'
      ctx.moveTo(centerX, centerY)
      const pointerLength = radius - 15
      const pointerX = centerX + pointerLength * Math.cos(angleRad)
      const pointerY = centerY + pointerLength * Math.sin(angleRad)
      ctx.lineTo(pointerX, pointerY)
      ctx.stroke()
    }

    // Note: Value text is now displayed via HTML input overlay, not canvas
  }, [angle, config])

  // Redraw when angle or config changes
  useEffect(() => {
    drawKnob()
  }, [drawKnob])

  // Handle mouse/touch start
  const handleStart = useCallback(
    (clientX: number, clientY: number) => {
      if (disabled || config.readOnly) return
      setIsDragging(true)
      const newAngle = calculateAngleFromEvent(clientX, clientY)
      setAngle(newAngle)
      onChange(newAngle)
    },
    [disabled, config.readOnly, calculateAngleFromEvent, onChange]
  )

  // Handle mouse/touch move
  const handleMove = useCallback(
    (clientX: number, clientY: number) => {
      if (!isDragging || disabled || config.readOnly) return
      const newAngle = calculateAngleFromEvent(clientX, clientY)
      setAngle(newAngle)
      onChange(newAngle)
    },
    [isDragging, disabled, config.readOnly, calculateAngleFromEvent, onChange]
  )

  // Handle mouse/touch end
  const handleEnd = useCallback(() => {
    setIsDragging(false)
  }, [])

  // Mouse event handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault()
    handleStart(e.clientX, e.clientY)
  }

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      handleMove(e.clientX, e.clientY)
    },
    [handleMove]
  )

  const handleMouseUp = useCallback(() => {
    handleEnd()
  }, [handleEnd])

  // Touch event handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault()
    const touch = e.touches[0]
    handleStart(touch.clientX, touch.clientY)
  }

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (e.touches.length > 0) {
        const touch = e.touches[0]
        handleMove(touch.clientX, touch.clientY)
      }
    },
    [handleMove]
  )

  const handleTouchEnd = useCallback(() => {
    handleEnd()
  }, [handleEnd])

  // Add global event listeners for drag
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      document.addEventListener('touchmove', handleTouchMove)
      document.addEventListener('touchend', handleTouchEnd)

      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        document.removeEventListener('touchmove', handleTouchMove)
        document.removeEventListener('touchend', handleTouchEnd)
      }
    }
  }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd])

  // Handle input field change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled || config.readOnly) return
    const newValue = normalizeValue(parseFloat(e.target.value) || config.min)
    setAngle(newValue)
    onChange(newValue)
  }

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative',
        display: 'table',
        textAlign: 'center',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        touchAction: 'none'
      }}
    >
      <canvas
        ref={canvasRef}
        width={config.width}
        height={config.height}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        style={{
          cursor: disabled || config.readOnly ? 'default' : 'pointer',
          display: 'block'
        }}
      />
      <input
        ref={inputRef}
        type="number"
        id={id}
        name={name}
        value={angle}
        onChange={handleInputChange}
        min={config.min}
        max={config.max}
        step={config.step}
        disabled={disabled}
        readOnly={config.readOnly}
        className="knob-input"
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: `${config.width * 0.6}px`,
          textAlign: 'center',
          border: 'none',
          background: 'transparent',
          fontSize: `${Math.floor(config.width / 5)}px`,
          fontWeight: config.fontWeight,
          fontFamily: config.font,
          color: config.fgColor,
          outline: 'none',
          padding: '2px',
          margin: '0',
          borderRadius: '3px',
          MozAppearance: 'textfield',
          WebkitAppearance: 'none',
          pointerEvents: config.readOnly || disabled ? 'none' : 'auto'
        }}
      />
      <style>{`
        .knob-input:focus {
          border: 2px solid ${config.fgColor} !important;
          background: rgba(255, 255, 255, 0.9) !important;
        }
      `}</style>
    </div>
  )
}

export default Knob
